import { useContext } from 'react';

import { useQuery } from '@apollo/client';
import type { ApolloError } from '@apollo/client';

import { getLocalizedQueryVariables } from 'bundles/internationalization-lib/components/WithLocalizedContent';
import {
  PromoTemplatePageContentSegmentOneFragmentDoc,
  PromoTemplatePageContentSegmentTwoFragmentDoc,
} from 'bundles/promo-template/__generated__/contentfulOperations';
import type {
  PromoTemplateArticlesCollectionFragment,
  PromoTemplateCannedCollectionFragment,
  PromoTemplateCitationFragment,
  PromoTemplateCollectionsFragment,
  PromoTemplateFaqsFragment,
  PromoTemplateHeroFragment,
  PromoTemplateInformationFragment,
  PromoTemplatePageContentSegmentOneFragment,
  PromoTemplatePageContentSegmentTwoFragment,
  PromoTemplatePartnerListFragment,
  PromoTemplatePromoBarFragment,
  PromoTemplateTestimonialCarouselFragment,
  PromoTemplateValuePropsFragment,
} from 'bundles/promo-template/__generated__/contentfulTypes';
import { PromoTemplateContext } from 'bundles/promo-template/contexts/PromoTemplateContext';
import type { GetPromoTemplateVariantContentQuery } from 'bundles/promo-template/queries/__generated__/getVariantContent';
import getVariantContent from 'bundles/promo-template/queries/getVariantContent.graphql';

export type Modules = Array<
  | PromoTemplateCannedCollectionFragment
  | PromoTemplateValuePropsFragment
  | PromoTemplatePartnerListFragment
  | PromoTemplateCollectionsFragment
  | PromoTemplateTestimonialCarouselFragment
  | PromoTemplateInformationFragment
  | PromoTemplateHeroFragment
  | PromoTemplateArticlesCollectionFragment
  | PromoTemplateFaqsFragment
  | PromoTemplateCitationFragment
  | PromoTemplatePromoBarFragment
>;

export type PromoTemplatePageData = {
  title: string;
  seoDescription?: string;
  seoTitle?: string;
  seoImageUrl?: string;
  seoCanonical?: string;
  preventSearchEngine: boolean;
  blocks: Modules;
};

type PropsFromGraphQL = {
  page?: PromoTemplatePageData;
  loading: boolean;
  error?: ApolloError;
};

const getMergeQueryData = (
  segmentOneData: PromoTemplatePageContentSegmentOneFragment,
  segmentTwoData: PromoTemplatePageContentSegmentTwoFragment
) => {
  const segmentOneDataItems = segmentOneData.modulesCollection?.items ?? [];
  const segmentTwoDataItems = segmentTwoData.modulesCollection?.items ?? [];
  const items = segmentOneDataItems.map((item, index) => (!item?.__typename ? segmentTwoDataItems[index] : item));

  return {
    ...segmentOneData,
    modulesCollection: {
      ...segmentOneData.modulesCollection,
      items,
    },
  };
};

export default function usePageContentQuery(slug: string, domain: string): PropsFromGraphQL {
  const { isPreview } = useContext(PromoTemplateContext);

  const segmentOneLocalizedQueryVars = getLocalizedQueryVariables({
    isPreview,
    contentType: 'promoTemplatePageContent',
    fragmentDoc: PromoTemplatePageContentSegmentOneFragmentDoc,
    fragmentFieldsName: 'PromoTemplatePageContentSegmentOne',
    filters: { slug, domain_contains_some: domain },
    contentfulSpace: 'marketing',
  });

  const segmentOneLocalizedVariantQueryVars = {
    ...segmentOneLocalizedQueryVars?.contentfulRequest?.variables,
    query: segmentOneLocalizedQueryVars?.contentfulRequest?.query,
    epicNamespace: 'localization',
    slug,
    contentfulSpace: 'marketing',
  };

  const {
    data: segmentOneData,
    loading: segmentOneLoading,
    error: segmentOneError,
  } = useQuery(getVariantContent, {
    variables: segmentOneLocalizedVariantQueryVars,
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
  });

  const segmentTwoLocalizedQueryVars = getLocalizedQueryVariables({
    isPreview,
    contentType: 'promoTemplatePageContent',
    fragmentDoc: PromoTemplatePageContentSegmentTwoFragmentDoc,
    fragmentFieldsName: 'PromoTemplatePageContentSegmentTwo',
    filters: { slug, domain_contains_some: domain },
    contentfulSpace: 'marketing',
  });

  const segmentTwoLocalizedVariantQueryVars = {
    ...segmentTwoLocalizedQueryVars?.contentfulRequest?.variables,
    query: segmentTwoLocalizedQueryVars?.contentfulRequest?.query,
    epicNamespace: 'localization',
    slug,
    contentfulSpace: 'marketing',
  };

  const {
    data: segmentTwoData,
    loading: segmentTwoLoading,
    error: segmentTwoError,
  } = useQuery(getVariantContent, {
    variables: segmentTwoLocalizedVariantQueryVars,
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
  });

  if (segmentOneLoading || segmentTwoLoading) {
    return {
      loading: true,
    };
  }

  /**
   * The query is too big for just one call! Contentful recommends
   * splitting large queries into smaller ones in this case.
   * https://www.contentful.com/developers/docs/references/graphql/#/reference/graphql-errors/graphql-errors-explained
   */
  const segmentOneContentData = (segmentOneData as GetPromoTemplateVariantContentQuery)?.LocalizedData
    ?.getVariantContent?.contentDataJson;
  const segmentTwoContentData = (segmentTwoData as GetPromoTemplateVariantContentQuery)?.LocalizedData
    ?.getVariantContent?.contentDataJson;

  if (!segmentOneContentData || !segmentTwoContentData) {
    return {
      page: undefined,
      loading: false,
      error: segmentOneError ?? segmentTwoError,
    };
  }

  const mergedQueryData = getMergeQueryData(segmentOneContentData, segmentTwoContentData);
  const blocks = (mergedQueryData?.modulesCollection?.items.filter((m) => !!m && m?.__typename) ?? []) as Modules;

  return {
    page: {
      title: mergedQueryData?.title ?? '',
      seoTitle: mergedQueryData?.seoTitle ?? '',
      seoDescription: mergedQueryData?.seoDescription ?? undefined,
      seoImageUrl: mergedQueryData?.seoImage?.url ?? undefined,
      seoCanonical: mergedQueryData?.seoCanonical ?? undefined,
      preventSearchEngine: mergedQueryData?.preventSearchEngine ?? false,
      blocks,
    },
    loading: false,
    error: segmentOneError ?? segmentTwoError,
  };
}
